import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalCloseButton,
  Box,
  Flex,
  IconButton,
  Text,
  Heading,
  useDisclosure
} from '@chakra-ui/react'
import { FC, useCallback, useEffect, useMemo, useState } from 'react'
import { BiExpandAlt } from 'react-icons/bi'
import { useImmer } from 'use-immer'

import useHome from '@hook/shared/use-home'

import LayoutEmpty from '@component/Layout/Empty'
import TableGrid from '@component/Layout/TableGrid'

import { getTableByEndpoint } from '@service/dashboard'

import ChartsLoading from './Loading'

interface OverviewTableProps {
  id: string
  title: string
  subtitle?: string
  // Não usado por pedidosAprovacao (dados vêm do HomeProvider)
  endpoint?: string
  entity?: string
  headers: any[]
  dataNew?: any[]
  parser: (data: any) => any[]
  parserFooter?: (data: any) => any[]
  isExpandable?: boolean
  empresasIds: Array<number>
  isActionFooter?: boolean
}

const OverviewTable: FC<OverviewTableProps> = ({
  id,
  title,
  subtitle,
  endpoint,
  parser,
  parserFooter,
  entity,
  headers = null,
  isExpandable = false,
  empresasIds,
  isActionFooter = false
}) => {
  const {
    getDataTable,
    getDataPurchaseOrders,
    loadingTable,
    loadingEncomenda,
    loadingSemVendas,
    loadingSlfp,
    loadingCarteira,
    loadingEstoque,
    loadingPurchaseOrders,

    dataTable,
    dataEncomenda,
    dataSemVendas,
    dataEstoqueExcesso,
    dataCarteiraFornecedor,
    dataSlsf,
    dataPurchaseOrders
  } = useHome()

  const { isOpen, onOpen, onClose } = useDisclosure()

  const [loading, setLoading] = useState(true)

  const [data, updateData] = useImmer([])

  const columns = useMemo(() => {
    if (!headers) {
      return []
    }

    return headers
  }, [headers])

  const getData = useCallback(
    async (
      options = {
        listagem: 15,
        classificacao: true,
        empresasIds: empresasIds
      }
    ) => {
      setLoading(true)
      try {
        const { data: res } = await getTableByEndpoint(endpoint, options)
        const { data } = res

        updateData(draft => {
          return draft.filter((row: any) => !!row.delete).concat(data)
        })
      } catch {}

      setLoading(false)
    },
    [updateData, endpoint, empresasIds]
  )

  const handleOpen = () => {
    onOpen()
    getData({
      listagem: 0,
      empresasIds: empresasIds
    })
  }

  useEffect(() => {
    switch (entity) {
      case 'vendas':
        if (dataTable.length === 0) {
          getDataTable(empresasIds, endpoint, entity)
          updateData(dataTable)
          setLoading(false)
        }
        break
      case 'encomenda':
        if (dataTable.length === 0) {
          getDataTable(empresasIds, endpoint, entity)
          updateData(dataEncomenda)
          setLoading(false)
        }
        break
      case 'slsf':
        if (dataTable.length === 0) {
          getDataTable(empresasIds, endpoint, entity)
          updateData(dataSlsf)
          setLoading(false)
        }
        break
      case 'semVendas':
        if (dataTable.length === 0) {
          getDataTable(empresasIds, endpoint, entity)
          updateData(dataSemVendas)
          setLoading(false)
        }
        break
      case 'estoque':
        if (dataTable.length === 0) {
          getDataTable(empresasIds, endpoint, entity)
          updateData(dataEstoqueExcesso)
          setLoading(false)
        }
        break
      case 'carteiraFornecedor':
        if (dataTable.length === 0) {
          getDataTable(empresasIds, endpoint, entity)
          updateData(dataCarteiraFornecedor)
          setLoading(false)
        }
        break
      case 'pedidosAprovacao':
        // Mesmos dados do gráfico: o provider deduplica a requisição em andamento
        if (dataPurchaseOrders.length === 0 && !loadingPurchaseOrders) {
          getDataPurchaseOrders(empresasIds)
        }
        break
      default:
        getData()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [empresasIds])

  return (
    <Box p={6} mx={3} mb={2} bg="white" borderRadius="8px" boxShadow="sm">
      <Flex
        alignItems="center"
        justifyContent="space-between"
        pb={!subtitle ? '21.4px' : 0}
        mb={6}
      >
        <Box>
          <Heading size="md">{title}</Heading>
          {subtitle && (
            <Text fontSize="sm" color="gray.600">
              {subtitle}
            </Text>
          )}
        </Box>
        {isExpandable && (
          <IconButton
            size="sm"
            variant="solid"
            onClick={handleOpen}
            isDisabled={loading}
            aria-label="Expandir"
            icon={<BiExpandAlt />}
          />
        )}
      </Flex>

      <>
        {(() => {
          if (entity === 'vendas') {
            return (
              <ChartsLoading isLoaded={!loadingTable}>
                {dataTable.length > 0 ? (
                  <TableGrid
                    id={id}
                    headers={columns}
                    data={parser(dataTable).slice(0, 15)}
                    itsSortableGroup={true}
                    isResizable={false}
                    dataFooter={isActionFooter ? parserFooter(dataTable) : null}
                    isFooter={isActionFooter}
                    height={'320px'}
                  />
                ) : (
                  <LayoutEmpty type="homeGrid" title="Nada encontrado!" />
                )}
              </ChartsLoading>
            )
          } else if (entity === 'encomenda') {
            return (
              <ChartsLoading isLoaded={!loadingEncomenda}>
                {dataEncomenda.length > 0 ? (
                  <TableGrid
                    id={id}
                    headers={columns}
                    data={parser(dataEncomenda).slice(0, 15)}
                    isResizable={false}
                    height={'320px'}
                  />
                ) : (
                  <LayoutEmpty type="homeGrid" title="Nada encontrado!" />
                )}
              </ChartsLoading>
            )
          } else if (entity === 'slsf') {
            return (
              <ChartsLoading isLoaded={!loadingSlfp}>
                {dataSlsf.length > 0 ? (
                  <TableGrid
                    id={id}
                    headers={columns}
                    data={parser(dataSlsf).slice(0, 15)}
                    isResizable={false}
                    height={'320px'}
                  />
                ) : (
                  <LayoutEmpty type="homeGrid" title="Nada encontrado!" />
                )}
              </ChartsLoading>
            )
          } else if (entity === 'semVendas') {
            return (
              <ChartsLoading isLoaded={!loadingSemVendas}>
                {dataSemVendas.length > 0 ? (
                  <TableGrid
                    id={id}
                    headers={columns}
                    data={parser(dataSemVendas).slice(0, 15)}
                    isResizable={false}
                    height={'320px'}
                  />
                ) : (
                  <LayoutEmpty type="homeGrid" title="Nada encontrado!" />
                )}
              </ChartsLoading>
            )
          } else if (entity === 'estoque') {
            return (
              <ChartsLoading isLoaded={!loadingEstoque}>
                {dataEstoqueExcesso.length > 0 ? (
                  <TableGrid
                    id={id}
                    headers={columns}
                    data={parser(dataEstoqueExcesso).slice(0, 15)}
                    isResizable={false}
                    height={'320px'}
                  />
                ) : (
                  <LayoutEmpty type="homeGrid" title="Nada encontrado!" />
                )}
              </ChartsLoading>
            )
          } else if (entity === 'carteiraFornecedor') {
            return (
              <ChartsLoading isLoaded={!loadingCarteira}>
                {dataCarteiraFornecedor.length > 0 ? (
                  <TableGrid
                    id={id}
                    headers={columns}
                    data={parser(dataCarteiraFornecedor).slice(0, 15)}
                    isResizable={false}
                    height={'320px'}
                  />
                ) : (
                  <LayoutEmpty type="homeGrid" title="Nada encontrado!" />
                )}
              </ChartsLoading>
            )
          } else if (entity === 'pedidosAprovacao') {
            const parsedData = parser(dataPurchaseOrders)
            return (
              <ChartsLoading isLoaded={!loadingPurchaseOrders}>
                {parsedData.length > 0 ? (
                  <TableGrid
                    id={id}
                    headers={columns}
                    data={parsedData}
                    isResizable={false}
                    height={'320px'}
                    dataFooter={
                      isActionFooter ? parserFooter(dataPurchaseOrders) : null
                    }
                    isFooter={isActionFooter}
                  />
                ) : (
                  <LayoutEmpty type="homeGrid" title="Nada encontrado!" />
                )}
              </ChartsLoading>
            )
          }
        })()}
      </>

      <Modal size="6xl" isOpen={isOpen} onClose={onClose}>
        <ModalOverlay />
        <ModalContent borderRadius={5}>
          <ModalHeader borderRadius="5px 5px 0 0" background="white">
            {title}
          </ModalHeader>
          <ModalCloseButton />
          <ModalBody pb={4} borderRadius="0 0 5px 5px" background="white">
            <ChartsLoading isLoaded={!loading}>
              <TableGrid
                id={`${id}-expanded`}
                headers={columns}
                data={parser(data)}
                height={'64vh'}
                dataFooter={isActionFooter ? parserFooter(data) : null}
                isActionFooter={isActionFooter}
                isPaged
              />
            </ChartsLoading>
          </ModalBody>
        </ModalContent>
      </Modal>
    </Box>
  )
}

export default OverviewTable
